import React, { useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { Box, CssBaseline, AppBar, Toolbar, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";

import Header from "../components/Header/Header";
import SideNavigationBar, {
  DRAWER_WIDTH,
  DRAWER_COLLAPSED_WIDTH,
} from "../components/SideNavigationBar/SideNavigationBar";
import { useLoaderData } from "react-router-dom";
import DomoReport from "../components/DomoReport/DomoReport";
import MetaTags from "../components/MetaTags/MetaTags";

const SIDEBAR_STORAGE_KEY = "sidebarCollapsed";

const Home = () => {
  const [selectedClient, setSelectedClient] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [authError, setAuthError] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_STORAGE_KEY) !== "false"
  );

  const drawerWidth = isCollapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH;

  const handleToggle = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      return next;
    });
  };

  const auth = useAuth();
  const userData = useLoaderData();
  const navigate = useNavigate();

  // Extract idpHint from URL params to preserve internal login flow
  const getIdpHintFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("idpHint");
  };

  const idpHint = getIdpHintFromUrl();

  useEffect(() => {
    console.log("Home -> useEffect()");

    if (!auth.isAuthenticated) {
      setAuthError(true);
      return;
    }

    // Only set default client on initial load, don't override user selection
    if (userData?.defaultClient && !selectedClient) {
      setSelectedClient(userData.defaultClient);
    }
  }, [auth.isAuthenticated, userData?.defaultClient]);

  // Restore persisted tab when the selected client changes
  useEffect(() => {
    if (selectedClient) {
      const stored = localStorage.getItem(`activeTab-${selectedClient}`);
      setActiveTab(stored !== null ? parseInt(stored) : 0);
    }
  }, [selectedClient]);

  const handleClientChange = (newState) => {
    setSelectedClient(newState);
  };

  const handleTabChange = (index) => {
    setActiveTab(index);
    if (selectedClient) {
      localStorage.setItem(`activeTab-${selectedClient}`, index);
    }
  };

  const handleRetryLogin = () => {
  const signinOptions = idpHint
      ? { extraQueryParams: { kc_idp_hint: idpHint } }
      : {};
    auth.signinRedirect(signinOptions); // Redirect the user to the login page
  };

  const currentPartnerTabs =
    userData.partners?.find((p) => p.name === selectedClient)?.urls || [];

  return (
    <>
      <MetaTags
        title="Fluent Pulse I AI-powered advertising & monetization platform"
        description="Leverage first-party data and advanced machine learning to deliver personalization and enhance brand connections with online shoppers."
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexGrow: 1,
        }}
      >
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            width: `calc(100% - ${drawerWidth}px)`,
            ml: `${drawerWidth}px`,
            background: "#0D1137",
            transition: "width 225ms cubic-bezier(0.4, 0, 0.6, 1), margin 225ms cubic-bezier(0.4, 0, 0.6, 1)",
          }}
        >
          <Toolbar>
            <Header
              data={userData}
              client={selectedClient}
              onChange={handleClientChange}
            />
          </Toolbar>
        </AppBar>
        <SideNavigationBar
          data={userData}
          isCollapsed={isCollapsed}
          onToggle={handleToggle}
          tabs={currentPartnerTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <Box
          sx={{
            width: "100%",
            height: "100vh",
            marginTop: "130px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {authError ? (
            <Box textAlign="center">
              <Typography variant="h6" color="error">
                The email and password combination are invalid. Please log in again.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={handleRetryLogin}
              >
                Retry Login
              </Button>
            </Box>
          ) : selectedClient ? (
            <DomoReport client={selectedClient} tab={activeTab} />
          ) : (
            <Typography variant="h6" color="error" align="center">
              Please contact your account manager to give you permission to see the report.
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Home;
